/**
 * NestPlatform — delivery zone for food items.
 *
 * When a player carrying food enters the nest zone, the food
 * is delivered to the crow. The nest provides visual feedback
 * of its current state (empty, partially filled, complete).
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { tagAs } from "../../levels/PlaceholderTag";

const DELIVERY_ZONE_VISIBILITY = 0.5;
const DELIVERY_ZONE_ALPHA = 0.32;
const DELIVERY_ZONE_PANEL_VISIBILITY = 0.75;
const DELIVERY_ZONE_PANEL_DEPTH = 0.04;
const DELIVERY_GUIDE_ALPHA = 0.72;
const DELIVERY_GUIDE_DEPTH = 0.06;
const DELIVERY_GUIDE_RAIL_WIDTH = 0.08;
const DELIVERY_ZONE_PANEL_Z = 1.02;
const DELIVERY_GUIDE_FRONT_Z = 1.08;

export interface NestPlatformConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  requiredCount?: number;
  showDeliveryZone?: boolean;
  deliveryRadius?: number;
  deliveryHeight?: number;
  deliveryYOffset?: number;
}

export class NestPlatform extends InteractiveObject {
  private deliveredCount = 0;
  private readonly requiredCount: number;

  /** The visual nest bowl mesh. */
  private nestBowl: Mesh;
  /** Small spheres representing delivered food items. */
  private foodVisuals: Mesh[] = [];
  private deliveryZoneMat: StandardMaterial | null = null;
  private deliveryGuideMat: StandardMaterial | null = null;

  constructor(scene: Scene, config: NestPlatformConfig) {
    const size = config.size ?? new Vector3(2.4, 0.4, 1.2);
    super(scene, { ...config, size });

    this.requiredCount = config.requiredCount ?? 1;

    // Nest bowl — a squashed hemisphere (inverted dome shape)
    this.nestBowl = MeshBuilder.CreateSphere(
      `nest_${config.key}_bowl`,
      { diameter: size.x, segments: 16, slice: 0.5 },
      scene,
    );
    this.nestBowl.parent = this.rootMesh;
    this.nestBowl.position = Vector3.Zero();
    this.nestBowl.scaling = new Vector3(1, 0.35, 0.6);
    this.nestBowl.rotation.x = Math.PI; // Invert so opening faces up

    const nestMat = new StandardMaterial(`nest_${config.key}_mat`, scene);
    nestMat.diffuseColor = new Color3(0.4, 0.26, 0.13); // Twig brown
    nestMat.specularColor = Color3.Black();
    nestMat.emissiveColor = new Color3(0.02, 0.01, 0.005);
    this.nestBowl.material = nestMat;
    this.material = nestMat;

    // Twig accents — thin cylinders along the rim
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const twig = MeshBuilder.CreateCylinder(
        `nest_${config.key}_twig_${i}`,
        { diameter: 0.04, height: size.x * 0.6, tessellation: 6 },
        scene,
      );
      twig.parent = this.rootMesh;
      twig.rotation.z = Math.PI / 2 + (i * 0.3 - 0.6);
      twig.position = new Vector3(
        Math.cos(angle) * size.x * 0.35,
        0.05,
        Math.sin(angle) * size.x * 0.15,
      );

      const twigMat = new StandardMaterial(`nest_twig_${i}_mat`, scene);
      twigMat.diffuseColor = new Color3(0.55, 0.36, 0.18);
      twigMat.specularColor = Color3.Black();
      twig.material = twigMat;
    }

    if (config.showDeliveryZone) {
      this.createDeliveryZoneGuide(config);
    }
  }

  private createDeliveryZoneGuide(config: NestPlatformConfig): void {
    const radius = config.deliveryRadius ?? 1.8;
    const height = config.deliveryHeight ?? 5.4;
    const yOffset = config.deliveryYOffset ?? -height * 0.3;
    const zone = MeshBuilder.CreateCylinder(
      `nest_${config.key}_delivery_zone`,
      { diameter: radius * 2, height, tessellation: 32 },
      this.scene,
    );
    zone.parent = this.rootMesh;
    zone.position = new Vector3(0, yOffset, 0);
    zone.isPickable = false;
    zone.visibility = DELIVERY_ZONE_VISIBILITY;

    this.deliveryZoneMat = new StandardMaterial(`nest_${config.key}_delivery_zone_mat`, this.scene);
    this.deliveryZoneMat.diffuseColor = new Color3(0.95, 0.72, 0.28);
    this.deliveryZoneMat.emissiveColor = new Color3(0.42, 0.26, 0.06);
    this.deliveryZoneMat.alpha = DELIVERY_ZONE_ALPHA;
    this.deliveryZoneMat.specularColor = Color3.Black();
    this.deliveryZoneMat.disableLighting = true;
    this.deliveryZoneMat.metadata = { preserveAffordanceEmissive: true };
    zone.material = this.deliveryZoneMat;
    tagAs(zone, "modular-final");

    const panel = MeshBuilder.CreateBox(
      `nest_${config.key}_delivery_zone_panel`,
      { width: radius * 2, height, depth: DELIVERY_ZONE_PANEL_DEPTH },
      this.scene,
    );
    panel.parent = this.rootMesh;
    panel.position = new Vector3(0, yOffset, DELIVERY_ZONE_PANEL_Z);
    panel.isPickable = false;
    panel.visibility = DELIVERY_ZONE_PANEL_VISIBILITY;
    panel.material = this.deliveryZoneMat;
    tagAs(panel, "modular-final");

    this.deliveryGuideMat = new StandardMaterial(`nest_${config.key}_delivery_guide_mat`, this.scene);
    this.deliveryGuideMat.diffuseColor = new Color3(1.0, 0.8, 0.32);
    this.deliveryGuideMat.emissiveColor = new Color3(0.62, 0.42, 0.1);
    this.deliveryGuideMat.alpha = DELIVERY_GUIDE_ALPHA;
    this.deliveryGuideMat.specularColor = Color3.Black();
    this.deliveryGuideMat.disableLighting = true;
    this.deliveryGuideMat.metadata = { preserveAffordanceEmissive: true };

    this.addDeliveryGuideBox(
      `nest_${config.key}_delivery_rail_left`,
      { width: DELIVERY_GUIDE_RAIL_WIDTH, height, depth: DELIVERY_GUIDE_DEPTH },
      new Vector3(-radius, yOffset, DELIVERY_GUIDE_FRONT_Z),
    );
    this.addDeliveryGuideBox(
      `nest_${config.key}_delivery_rail_right`,
      { width: DELIVERY_GUIDE_RAIL_WIDTH, height, depth: DELIVERY_GUIDE_DEPTH },
      new Vector3(radius, yOffset, DELIVERY_GUIDE_FRONT_Z),
    );

    const rungWidth = radius * 2;
    const halfHeight = height / 2;
    this.addDeliveryGuideBox(
      `nest_${config.key}_delivery_rung_bottom`,
      { width: rungWidth, height: DELIVERY_GUIDE_RAIL_WIDTH, depth: DELIVERY_GUIDE_DEPTH },
      new Vector3(0, yOffset - halfHeight, DELIVERY_GUIDE_FRONT_Z),
    );
    this.addDeliveryGuideBox(
      `nest_${config.key}_delivery_rung_mid`,
      { width: rungWidth, height: DELIVERY_GUIDE_RAIL_WIDTH, depth: DELIVERY_GUIDE_DEPTH },
      new Vector3(0, yOffset, DELIVERY_GUIDE_FRONT_Z),
    );
    this.addDeliveryGuideBox(
      `nest_${config.key}_delivery_rung_top`,
      { width: rungWidth, height: DELIVERY_GUIDE_RAIL_WIDTH, depth: DELIVERY_GUIDE_DEPTH },
      new Vector3(0, yOffset + halfHeight, DELIVERY_GUIDE_FRONT_Z),
    );
  }

  private addDeliveryGuideBox(
    name: string,
    size: { width: number; height: number; depth: number },
    position: Vector3,
  ): void {
    if (!this.deliveryGuideMat) return;

    const guide = MeshBuilder.CreateBox(name, size, this.scene);
    guide.parent = this.rootMesh;
    guide.position = position;
    guide.isPickable = false;
    guide.material = this.deliveryGuideMat;
    tagAs(guide, "modular-final");
  }

  /** Record a food delivery. Returns true if all required food delivered. */
  deliverFood(): boolean {
    this.deliveredCount++;

    // Add visual food sphere inside the nest
    const food = MeshBuilder.CreateSphere(
      `nestFood_${this.key}_${this.deliveredCount}`,
      { diameter: 0.25, segments: 8 },
      this.scene,
    );
    food.parent = this.rootMesh;
    food.position = new Vector3(
      (this.deliveredCount - 1) * 0.3 - 0.15,
      0.15,
      0,
    );
    const fMat = new StandardMaterial(`nestFoodMat_${this.deliveredCount}`, this.scene);
    fMat.diffuseColor = new Color3(0.7, 0.35, 0.1);
    fMat.emissiveColor = new Color3(0.05, 0.02, 0.01);
    fMat.specularColor = Color3.Black();
    food.material = fMat;
    this.foodVisuals.push(food);

    if (this.deliveredCount >= this.requiredCount) {
      this.solve();
      return true;
    }

    this.state = "active";
    this.updateMaterialForState();
    return false;
  }

  get foodCount(): number {
    return this.deliveredCount;
  }

  get isComplete(): boolean {
    return this.deliveredCount >= this.requiredCount;
  }

  override dispose(): void {
    super.dispose();
    this.deliveryZoneMat?.dispose();
    this.deliveryGuideMat?.dispose();
    this.deliveryZoneMat = null;
    this.deliveryGuideMat = null;
  }
}
